// ReportExportManager.tsx
import React, { useEffect } from 'react';

import { getBasePath } from '@ais-ui/api';
import { isArray } from 'lodash';
import { AsyncJobStatus, useAsyncJobs } from '@ais-ui/async-jobs';
import {
  ExportFormat,
  useCompleteReportExportFetchingProcess,
} from '@ais-ui/websockets';

// Global registry to track export jobs being managed by this component
// This ensures we don't try to start the same job multiple times from different instances
const exportJobRegistry = new Set<string>();

export const ReportExportManager: React.FC = () => {
  const { jobs, updateJob } = useAsyncJobs();
  const exportProcess = useCompleteReportExportFetchingProcess();

  // Find a pending export job to work on
  useEffect(() => {
    // Skip if we're already busy with an export
    if (exportProcess.isLoading) {
      return;
    }

    // Find a pending export job that isn't being handled yet
    const pendingJob = jobs.find(
      (job) =>
        job.metadata?.type === 'report-export' &&
        (job.status === AsyncJobStatus.PENDING ||
          job.status === AsyncJobStatus.RUNNING) &&
        !exportJobRegistry.has(job.id)
    );

    if (pendingJob) {
      exportJobRegistry.add(pendingJob.id);

      // Start the export process
      exportProcess.startJob({
        jobId: pendingJob.metadata?.reportId,
        format: pendingJob.metadata?.format,
        centerLat: pendingJob.metadata?.centerLat,
        centerLon: pendingJob.metadata?.centerLon,
        zoomLevel: pendingJob.metadata?.zoomLevel,
        minLat: pendingJob.metadata?.minLat,
        minLon: pendingJob.metadata?.minLon,
        maxLat: pendingJob.metadata?.maxLat,
        maxLon: pendingJob.metadata?.maxLon,
      });

      // Update job to show it's running
      updateJob(pendingJob.id, {
        status: AsyncJobStatus.RUNNING,
        startedAt: new Date(),
      });
    }
  }, [jobs, exportProcess, updateJob]);

  // Handle export success
  useEffect(() => {
    if (exportProcess.data) {
      // Find the job that was being processed
      const runningJob = jobs.find(
        (job) =>
          job.status === AsyncJobStatus.RUNNING && exportJobRegistry.has(job.id)
      );

      if (runningJob) {
        let downloadUrl: string | undefined;
        const format = runningJob.metadata?.format as ExportFormat;

        if (format === 'OF_SHIPLIST' && isArray(exportProcess.data)) {
          const csvContent =
            'name,mmsi,imo\n' +
            exportProcess.data
              .map((item) => `${item.name},${item.mmsi},${item.imo}`)
              .join('\n');
          const blob = new Blob([csvContent], { type: 'text/csv' });
          downloadUrl = URL.createObjectURL(blob);
        } else {
          const baseUrl = getBasePath();
          downloadUrl = baseUrl + exportProcess.data;
        }

        // Update the job to completed status
        updateJob(runningJob.id, {
          status: AsyncJobStatus.COMPLETED,
          completedAt: new Date(),
          result: downloadUrl,
          progress: 100,
        });

        // Create and click the anchor to download the file
        const anchor = document.createElement('a');
        anchor.href = downloadUrl;

        const reportInfo = runningJob.metadata?.reportInfo;
        if (format === 'OF_SHIPLIST' && reportInfo) {
          const reportOwner = reportInfo['Owner']
            .replace(/\(.*?\)/g, '')
            .trim();
          anchor.download = `${reportInfo['Job name']}-${reportInfo['Report Definition']}-${reportOwner}-job${reportInfo['Job ID']}.csv`;
        } else if (format === 'OF_SHIPLIST') {
          anchor.download = `exported_ship_list.csv`;
        } else {
          const filename = decodeURIComponent(
            downloadUrl.split('/').pop() || 'report'
          );
          anchor.download = filename;
        }

        anchor.click();

        exportJobRegistry.delete(runningJob.id);
      }
    }
  }, [exportProcess.data, jobs, updateJob]);

  useEffect(() => {
    if (exportProcess.isError) {
      const runningJob = jobs.find(
        (job) =>
          job.status === AsyncJobStatus.RUNNING && exportJobRegistry.has(job.id)
      );

      if (runningJob) {
        updateJob(runningJob.id, {
          status: AsyncJobStatus.FAILED,
          completedAt: new Date(),
          error: {
            message: 'Export failed',
          },
        });
        exportJobRegistry.delete(runningJob.id);
      }
    }
  }, [exportProcess.isError, jobs, updateJob]);

  return null;
};
